import * as React from "react";
import { 
  Bold, Italic, Underline, Strikethrough, 
  Heading1, Heading2, Heading3, Pilcrow,
  AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Quote, Link2, ImagePlus,
  Palette, Highlighter, Code
} from "lucide-react";
import { apiClient } from "../lib/api";

/**
 * 이미지 압축 및 리사이즈 함수
 * 최대 가로 1920px, JPEG 품질 85%로 최적화하여 파일 크기 감소
 */
async function compressImage(file: File, maxWidth: number = 1920, quality: number = 0.85): Promise<File> {
  return new Promise((resolve) => {
    // 이미지가 아닌 경우 원본 반환
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    // 파일 크기가 500KB 미만이면 압축하지 않음 (이미 작은 파일)
    if (file.size < 500 * 1024) {
      resolve(file);
      return;
    }

    // Canvas 크기 제한 체크 (일부 브라우저는 최대 크기 제한이 있음)
    const MAX_CANVAS_SIZE = 16384; // 일반적인 브라우저 제한

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          // 원본 크기 확인
          const originalWidth = img.width;
          const originalHeight = img.height;

          // Canvas 크기 제한 체크
          if (originalWidth > MAX_CANVAS_SIZE || originalHeight > MAX_CANVAS_SIZE) {
            console.warn('이미지가 너무 커서 압축하지 않고 원본을 사용합니다.');
            resolve(file);
            return;
          }

          // 최대 크기보다 작으면 압축만 수행
          if (originalWidth <= maxWidth) {
            // 품질만 조정하여 압축
            const canvas = document.createElement('canvas');
            canvas.width = originalWidth;
            canvas.height = originalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve(file);
              return;
            }
            ctx.drawImage(img, 0, 0);
            
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  console.warn('이미지 압축 실패, 원본 사용');
                  resolve(file);
                  return;
                }
                const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              },
              'image/jpeg',
              quality
            );
            return;
          }

          // 리사이즈 필요
          const aspectRatio = originalHeight / originalWidth;
          const newWidth = Math.min(maxWidth, originalWidth);
          const newHeight = Math.round(newWidth * aspectRatio);

          // 리사이즈 후 크기도 Canvas 제한 체크
          if (newWidth > MAX_CANVAS_SIZE || newHeight > MAX_CANVAS_SIZE) {
            console.warn('리사이즈 후 이미지가 너무 커서 압축하지 않고 원본을 사용합니다.');
            resolve(file);
            return;
          }

          const canvas = document.createElement('canvas');
          canvas.width = newWidth;
          canvas.height = newHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }

          // 고품질 리사이즈
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, newWidth, newHeight);

          // JPEG로 변환 및 압축
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                console.warn('이미지 압축 실패, 원본 사용');
                resolve(file);
                return;
              }
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        } catch (error) {
          console.error('이미지 압축 중 오류:', error);
          resolve(file); // 에러 시 원본 반환
        }
      };
      img.onerror = () => {
        console.warn('이미지 로드 실패, 원본 사용');
        resolve(file); // 에러 시 원본 반환
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      console.warn('파일 읽기 실패, 원본 사용');
      resolve(file); // 에러 시 원본 반환
    };
    reader.readAsDataURL(file);
  });
}

function exec(cmd: string, value?: string) {
  // deprecated지만 "간단 리치 텍스트" 목적엔 가장 가벼움 (추가 deps 없음)
  document.execCommand(cmd, false, value);
}

// URL 감지 및 변환 함수 (텍스트 노드의 URL을 링크로 변환)
function autoLinkifyText(el: HTMLElement): void {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  const textNodes: Text[] = [];
  let node;
  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      textNodes.push(node as Text);
    }
  }
  
  textNodes.forEach((textNode) => {
    const text = textNode.textContent || "";
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;
    const matches = Array.from(text.matchAll(urlRegex));
    
    if (matches.length === 0) return;
    
    // 부모가 이미 링크인 경우 건너뛰기
    let parent = textNode.parentElement;
    while (parent && parent !== el) {
      if (parent.tagName === "A") return;
      parent = parent.parentElement;
    }
    
    // URL을 링크로 변환
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    
    matches.forEach((match) => {
      if (match.index === undefined) return;
      
      // URL 앞의 텍스트
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
      }
      
      // URL 링크 생성
      const link = document.createElement("a");
      link.href = match[0];
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = match[0];
      fragment.appendChild(link);
      
      lastIndex = match.index + match[0].length;
    });
    
    // 나머지 텍스트
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
    }
    
    // 원본 텍스트 노드를 fragment로 교체
    textNode.parentNode?.replaceChild(fragment, textNode);
  });
}

async function uploadAndInsertImage(file: File, onChange: (html: string) => void, el: HTMLDivElement | null, setUploading: (v: boolean) => void) {
  const selection = window.getSelection();
  const range = selection?.rangeCount && el?.contains(selection.getRangeAt(0).commonAncestorContainer)
    ? selection.getRangeAt(0).cloneRange() : null;
  setUploading(true);
  try {
    // 파일 크기 체크 (10MB 제한)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert(`이미지 파일이 너무 큽니다 (최대 10MB). 현재 크기: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    // 이미지 압축 처리 (실패 시 원본 사용)
    let fileToUpload = file;
    try {
      // 압축 시도 (타임아웃 10초)
      fileToUpload = await Promise.race([
        compressImage(file, 1920, 0.85),
        new Promise<File>((_, reject) => 
          setTimeout(() => reject(new Error('압축 타임아웃')), 10000)
        )
      ]);
    } catch (error) {
      console.warn('이미지 압축 실패, 원본 파일로 업로드:', error);
      fileToUpload = file;
    }
    
    // 압축 후에도 크기 체크
    if (fileToUpload.size > maxSize) {
      console.warn('압축 후에도 파일이 너무 큼, 원본 사용');
      fileToUpload = file;
    }
    
    const res = await apiClient.uploadCommunityEditorImage(fileToUpload);
    // Keep async uploads in their original editor, even if focus moved to another block.
    if (!el?.isConnected) return;
    el.focus();
    const currentSelection = window.getSelection();
    const target = range && el.contains(range.commonAncestorContainer) ? range : document.createRange();
    if (target !== range) { target.selectNodeContents(el); target.collapse(false); }
    currentSelection?.removeAllRanges();
    currentSelection?.addRange(target);
    exec("insertImage", res.url);
    onChange(el.innerHTML);
  } catch (error: any) {
    console.error('이미지 업로드 실패:', error);
    const errorMessage = error?.message || '알 수 없는 오류';
    if (errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch')) {
      alert('이미지 업로드에 실패했습니다. 네트워크 연결을 확인하거나 잠시 후 다시 시도해주세요.');
    } else if (errorMessage.includes('크기') || errorMessage.includes('size')) {
      alert('이미지 파일이 너무 큽니다. 더 작은 이미지를 사용해주세요.');
    } else {
      alert(`이미지 업로드에 실패했습니다: ${errorMessage}`);
    }
  } finally {
    setUploading(false);
  }
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  compact = false,
  onInsertContent,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  compact?: boolean;
  onInsertContent?: (before: string, after: string) => void;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const colorRef = React.useRef<HTMLInputElement | null>(null);
  const bgRef = React.useRef<HTMLInputElement | null>(null);
  
  const [uploading, setUploading] = React.useState(false);
  const [htmlMode, setHtmlMode] = React.useState(false);
  const autoLinkTimeoutRef = React.useRef<number | null>(null);
  const changeRef = React.useRef(onChange);
  changeRef.current = onChange;
  const emitChange = React.useCallback((html:string)=>changeRef.current(html),[]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // 외부 state가 바뀌었을 때만 동기화
    if (el.innerHTML !== value) {
      if (autoLinkTimeoutRef.current) window.clearTimeout(autoLinkTimeoutRef.current);
      el.innerHTML = value || "";
    }
  }, [value, htmlMode]);

  const handleInput = React.useCallback((e: React.FormEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const html = el.innerHTML;
    
    // 디바운스하여 자동 링크 변환 (입력 후 800ms 후)
    if (autoLinkTimeoutRef.current) {
      window.clearTimeout(autoLinkTimeoutRef.current);
    }
    
    autoLinkTimeoutRef.current = window.setTimeout(() => {
      const currentEl = ref.current;
      if (!currentEl) return;
      
      // 커서 위치 저장
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      
      // 자동 링크 변환
      autoLinkifyText(currentEl);
      
      // 커서 위치 복원 시도
      if (range) {
        try {
          selection?.removeAllRanges();
          selection?.addRange(range);
        } catch (e) {
          // 커서 위치 복원 실패 시 무시
        }
      }
      
      emitChange(currentEl.innerHTML);
    }, 800) as any;
    
    emitChange(html);
  }, [emitChange]);

  React.useEffect(() => {
    return () => {
      if (autoLinkTimeoutRef.current) {
        window.clearTimeout(autoLinkTimeoutRef.current);
      }
    };
  }, []);

  const Button = ({ onClick, children, title }: { onClick: () => void, children: React.ReactNode, title?: string }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="p-1.5 rounded-lg text-gray-700 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 transition-all focus:outline-none"
    >
      {children}
    </button>
  );

  const Divider = () => <div className="w-px h-6 bg-gray-300 mx-1 my-auto rounded-full" />;

  return (
    <div className={`rounded-2xl border border-gray-200 overflow-hidden bg-white group focus-within:ring-2 focus-within:ring-[#a7bb82] focus-within:border-transparent transition-all ${compact ? "fe-content-text-fragment" : ""}`}>
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50/80 border-b border-gray-100 backdrop-blur-sm sticky top-0 z-10 transition-colors group-focus-within:bg-gray-50">
        
        {onInsertContent && <button type="button" className="fe-content-insert-at-cursor" onMouseDown={e=>e.preventDefault()} onClick={()=>{
          const el=ref.current;if(!el)return;
          const selection=window.getSelection();const current=selection?.rangeCount?selection.getRangeAt(0):null;
          if(!current||!el.contains(current.startContainer)||!el.contains(current.endContainer)){onInsertContent(el.innerHTML,"");return;}
          const before=current.cloneRange();before.selectNodeContents(el);before.setEnd(current.startContainer,current.startOffset);
          const after=current.cloneRange();after.selectNodeContents(el);after.setStart(current.startContainer,current.startOffset);
          const a=document.createElement('div'),b=document.createElement('div');a.append(before.cloneContents());b.append(after.cloneContents());onInsertContent(a.innerHTML,b.innerHTML);
        }}>＋ 여기에 내 콘텐츠 넣기</button>}
        {/* Headings */}
        <Button onClick={() => exec("formatBlock", "<H1>")} title="제목 1 (가장 큼)"><Heading1 className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("formatBlock", "<H2>")} title="제목 2 (중간)"><Heading2 className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("formatBlock", "<H3>")} title="제목 3 (약간 큼)"><Heading3 className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("formatBlock", "<P>")} title="본문 (기본)"><Pilcrow className="w-[18px] h-[18px]" /></Button>
        <Divider />

        {/* Text Formatting */}
        <Button onClick={() => exec("bold")} title="굵게 (Ctrl+B)"><Bold className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("italic")} title="기울임 (Ctrl+I)"><Italic className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("underline")} title="밑줄 (Ctrl+U)"><Underline className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("strikeThrough")} title="취소선"><Strikethrough className="w-[18px] h-[18px]" /></Button>
        <Divider />

        {/* Colors */}
        <div className="relative flex items-center">
          <Button onClick={() => colorRef.current?.click()} title="글자 색상"><Palette className="w-[18px] h-[18px] text-gray-700" /></Button>
          <input 
            type="color" 
            ref={colorRef}
            className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
            onChange={(e) => exec("foreColor", e.target.value)}
          />
        </div>
        <div className="relative flex items-center">
          <Button onClick={() => bgRef.current?.click()} title="배경 색상(형광펜)"><Highlighter className="w-[18px] h-[18px] text-gray-700" /></Button>
          <input 
            type="color" 
            ref={bgRef}
            className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
            onChange={(e) => exec("hiliteColor", e.target.value)}
          />
        </div>
        <Divider />
        
        {/* Alignment */}
        <Button onClick={() => exec("justifyLeft")} title="왼쪽 정렬"><AlignLeft className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("justifyCenter")} title="가운데 정렬"><AlignCenter className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("justifyRight")} title="오른쪽 정렬"><AlignRight className="w-[18px] h-[18px]" /></Button>
        <Divider />

        {/* Lists & Quotes */}
        <Button onClick={() => exec("insertUnorderedList")} title="기호 목록"><List className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("insertOrderedList")} title="번호 목록"><ListOrdered className="w-[18px] h-[18px]" /></Button>
        <Button onClick={() => exec("formatBlock", "<BLOCKQUOTE>")} title="인용구"><Quote className="w-[18px] h-[18px]" /></Button>
        <Divider />

        {/* Links & Media */}
        <Button
          title="링크 삽입"
          onClick={() => {
            const url = prompt("링크 주소를 입력하세요 (http:// 또는 https:// 포함)");
            if (!url) return;
            exec("createLink", url);
          }}
        >
          <Link2 className="w-[18px] h-[18px]" />
        </Button>
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="p-1.5 rounded-lg text-gray-700 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 transition-all focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          title="이미지 업로드"
        >
          <ImagePlus className="w-[18px] h-[18px]" />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            if (!file) return;
            const el = ref.current;
            await uploadAndInsertImage(file, emitChange, el, setUploading);
            input.value = "";
          }}
        />
        <Divider />
        {/* HTML Source Toggle */}
        <button
          type="button"
          onClick={() => {
            setHtmlMode((prev) => !prev);
          }}
          title={htmlMode ? "편집기 모드로 전환" : "HTML 소스 보기"}
          className={`p-1.5 rounded-lg transition-all focus:outline-none ${
            htmlMode
              ? 'bg-[#e5ecd4] text-[#435536] border border-[#b9c999] shadow-sm'
              : 'text-gray-700 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200'
          }`}
        >
          <Code className="w-[18px] h-[18px]" />
        </button>
      </div>

      {htmlMode ? (
        <textarea
          value={value}
          onChange={(e) => emitChange(e.target.value)}
          className="p-5 min-h-[400px] w-full text-sm leading-relaxed focus:outline-none text-gray-800 font-mono bg-gray-50 resize-y"
          aria-label="HTML 소스 코드"
          placeholder="HTML 소스 코드를 입력하세요"
          spellCheck={false}
        />
      ) : (
        <div
          ref={ref}
          contentEditable
          role="textbox"
          aria-label="게시글 내용"
          aria-multiline="true"
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={async (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            const images: File[] = [];
            for (const it of Array.from(items)) {
              if (it.kind === "file") {
                const f = it.getAsFile();
                if (f && f.type.startsWith("image/")) images.push(f);
              }
            }
            if (!images.length) return;
            e.preventDefault();
            const el = ref.current;
            for (const img of images) {
              await uploadAndInsertImage(img, emitChange, el, setUploading);
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={async (e) => {
            const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
            if (!files.length) return;
            e.preventDefault();
            const el = ref.current;
            for (const f of files) {
              await uploadAndInsertImage(f, emitChange, el, setUploading);
            }
          }}
          className="p-5 min-h-[400px] text-base leading-relaxed focus:outline-none text-gray-800"
          data-placeholder={placeholder || "내용을 입력해주세요"}
        />
      )}
      <style>{`
        /* Placeholder Styling */
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          display: block; /* For empty state block alignment */
        }
        
        /* Link styling inside editor */
        [contenteditable] a {
          color: #4f46e5;
          text-decoration: underline;
          cursor: text; /* So they can edit rather than click */
        }
        [contenteditable] a:hover {
          color: #4338ca;
        }

        /* Essential Editor Base Typography (maps to standard headings visually while editing) */
        [contenteditable] h1 { font-size: 1.875rem; font-weight: 800; line-height: 1.2; margin-top: 1.5em; margin-bottom: 0.5em; }
        [contenteditable] h2 { font-size: 1.5rem; font-weight: 700; line-height: 1.3333333; margin-top: 1.5em; margin-bottom: 0.5em; }
        [contenteditable] h3 { font-size: 1.25rem; font-weight: 600; line-height: 1.6; margin-top: 1.2em; margin-bottom: 0.4em; }
        [contenteditable] blockquote { font-style: italic; font-weight: 500; border-left-width: 0.25rem; border-color: #e5e7eb; padding-left: 1rem; margin-top: 1.6em; margin-bottom: 1.6em; color: #111827; }
        [contenteditable] ul { list-style-type: disc; padding-left: 1.625em; margin-top: 1.25em; margin-bottom: 1.25em; }
        [contenteditable] ol { list-style-type: decimal; padding-left: 1.625em; margin-top: 1.25em; margin-bottom: 1.25em; }
        [contenteditable] p { margin-top: 0.75em; margin-bottom: 0.75em; }

        /* Ensure images are responsive within the editor */
        [contenteditable] img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
      `}</style>
    </div>
  );
}



